import { v4 } from "uuid";
import Bold from "@tiptap/extension-bold";
import Text from "@tiptap/extension-text";
import Italic from "@tiptap/extension-italic";
import Document from "@tiptap/extension-document";
import Highlight from "@tiptap/extension-highlight";
import Paragraph from "@tiptap/extension-paragraph";
import Underline from "@tiptap/extension-underline";
import HardBreak from "@tiptap/extension-hard-break";
import { Dropcursor, Gapcursor, Placeholder, TrailingNode, UndoRedo } from "@tiptap/extensions";
import { BulletList, ListItem, ListKeymap, OrderedList } from "@tiptap/extension-list";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { DOMSerializer, Fragment, Slice, type Node as ProseMirrorNode } from "@tiptap/pm/model";
import {
  Decoration,
  Extension,
  Node,
  mergeAttributes,
  type Editor,
  type Extensions,
  type WidgetDecorationOptions,
} from "@tiptap/core";
import type { ScratchpadSource } from "./document";

export interface ScratchpadBlockMetadataOptions {
  renderWidget: (options: ScratchpadWidgetOptions) => Decoration;
  onRedirectTo?: (source: ScratchpadSource) => void;
  // The most recently captured source, so its new data point can focus its label. Read reactively by the widget.
  latestAddedDataPointSource?: () => ScratchpadSource | null;
}

// Each widget receives the extension's options (except the renderer itself) plus its block's details.
export interface ScratchpadBlockMetadataProps extends Omit<ScratchpadBlockMetadataOptions, "renderWidget"> {
  id: string;
  createdAt: string;
  updatedAt: string | null;
  isActive: boolean;
  isDatapoint: boolean;
  source: ScratchpadSource | null;
  datapointLabel: string;
  updateDatapointLabel: (label: string) => void;
  focusContent: () => boolean;
  readonly: boolean;
}

export interface ScratchpadWidgetOptions extends WidgetDecorationOptions {
  editor: Editor;
  pos: number;
  key: string;
  props: ScratchpadBlockMetadataProps;
}

export interface ScratchpadExtensionsOptions {
  placeholder?: string;
  blockMetadata: ScratchpadBlockMetadataOptions;
}

// Copies the source content as rendered, so each app copies the speaker names and text it displays.
export async function copyScratchpadSource(content: HTMLElement) {
  await navigator.clipboard.writeText(content.innerText.trim());
}

export function createScratchpadExtensions({
  placeholder = "Write notes or add a data point",
  blockMetadata,
}: ScratchpadExtensionsOptions): Extensions {
  return [
    Bold,
    BulletList,
    Document,
    Dropcursor,
    Gapcursor,
    HardBreak,
    UndoRedo,
    Italic,
    ListItem,
    ListKeymap,
    OrderedList,
    Paragraph,
    Text,
    Underline,
    TrailingNode,
    Placeholder.configure({
      showOnlyCurrent: false,
      // Read-only editors only show the placeholder for an empty document, and never on empty data points.
      showOnlyWhenEditable: false,
      placeholder: ({ editor, node }) => {
        if (editor.isEditable && node.type.name === "dataPoint") return "Add a note";
        else if (editor.isEditable || editor.isEmpty) return placeholder;
        else return "";
      },
    }),
    Highlight.configure({ multicolor: true }),
    DataPoint,
    createBlockMetadata(blockMetadata),
  ];
}

const BLOCK_TYPES = ["paragraph", "heading", "bulletList", "orderedList", "dataPoint"];

function freshContent(fragment: Fragment): Fragment {
  const nodes: ProseMirrorNode[] = [];
  fragment.forEach((node) => {
    nodes.push(
      node.isText
        ? node
        : node.type.create(
            BLOCK_TYPES.includes(node.type.name)
              ? { ...node.attrs, id: null, createdAt: null, updatedAt: null }
              : node.attrs,
            freshContent(node.content),
            node.marks,
          ),
    );
  });
  return Fragment.fromArray(nodes);
}

const isWholeChild = (slice: Slice, index: number) =>
  (index > 0 || slice.openStart === 0) && (index < slice.content.childCount - 1 || slice.openEnd === 0);

function getWidgetMetadata(node: ProseMirrorNode) {
  const { id, createdAt, updatedAt } = node.attrs;
  if (typeof id !== "string" || typeof createdAt !== "string") return;

  return {
    id,
    createdAt,
    updatedAt: typeof updatedAt === "string" ? updatedAt : null,
  };
}

export const DataPoint = Node.create({
  name: "dataPoint",
  group: "block",
  content: "inline*",
  addProseMirrorPlugins() {
    const clipboardSerializer = DOMSerializer.fromSchema(this.editor.schema);
    return [
      new Plugin({
        key: new PluginKey("scratchpadDataPointClipboard"),
        props: {
          // Data points only come from captures, so the clipboard carries them as plain paragraphs.
          clipboardSerializer: new DOMSerializer(
            { ...clipboardSerializer.nodes, [this.name]: clipboardSerializer.nodes.paragraph },
            clipboardSerializer.marks,
          ),
          // Pasted HTML has no data points, so whole ones can only come from a drag within this editor.
          transformPasted: (slice) => {
            const nodes: ProseMirrorNode[] = [];

            slice.content.forEach((node, _offset, index) => {
              const partial = node.type.name === "dataPoint" && !isWholeChild(slice, index);
              nodes.push(partial ? node.type.schema.nodes.paragraph.create(null, node.content) : node);
            });

            return new Slice(Fragment.fromArray(nodes), slice.openStart, slice.openEnd);
          },
          // A slice cut through a data point still carries its wrapper, which would otherwise be dropped as a new data point.
          handleDrop: (_view, _event, slice, moved) => {
            if (moved) return false;

            for (let index = 0; index < slice.content.childCount; index++) {
              if (slice.content.child(index).type.name === "dataPoint" && isWholeChild(slice, index)) return true;
            }

            return false;
          },
        },
      }),
    ];
  },
  addAttributes: () => ({
    label: {
      default: "",
      renderHTML: (attributes) => ({ "data-label": String(attributes.label ?? "") }),
    },
    source: { default: null, rendered: false },
  }),
  renderHTML: ({ HTMLAttributes }) => ["div", mergeAttributes(HTMLAttributes, { "data-scratchpad-point": "" }), 0],
  addKeyboardShortcuts() {
    return {
      // Without this, Enter would split one data point into two.
      Enter: () => {
        const { $from } = this.editor.state.selection;
        if ($from.parent.type.name !== this.name) return false;

        const after = $from.after($from.depth);
        return this.editor
          .chain()
          .insertContentAt(after, { type: "paragraph" })
          .setTextSelection(after + 1)
          .focus()
          .run();
      },
    };
  },
});

const BlockMetadata = Extension.create<ScratchpadBlockMetadataOptions>({
  name: "scratchpadBlockMetadata",
  addGlobalAttributes: () => [
    {
      types: BLOCK_TYPES,
      attributes: {
        id: { default: null, rendered: false },
        createdAt: { default: null, rendered: false },
        updatedAt: { default: null, rendered: false },
      },
    },
  ],
  addDecorations() {
    const editor = this.editor;
    const onRedirectTo = this.options.onRedirectTo;
    const renderWidget = this.options.renderWidget;
    const latestAddedDataPointSource = this.options.latestAddedDataPointSource;

    function currentDataPoint(id: string) {
      if (editor.isDestroyed) return;

      let pos = 0;
      const { doc } = editor.state;
      for (let index = 0; index < doc.childCount; index++) {
        const node = doc.child(index);
        if (node.attrs.id === id && node.type.name === "dataPoint") return { node, pos };
        pos += node.nodeSize;
      }
    }

    return {
      shouldUpdate: ({ tr, oldState, newState }) => tr.docChanged || !oldState.selection.eq(newState.selection),
      create: ({ state }) => {
        const decorations: Decoration[] = [];
        const { $from } = state.selection;
        const activePos = $from.depth > 0 ? $from.before(1) : undefined;

        state.doc.forEach((node, pos) => {
          const metadata = getWidgetMetadata(node);
          if (metadata == null) return;

          decorations.push(
            Decoration.Node(pos, pos + node.nodeSize, { class: "scratchpad-block" }),
            renderWidget({
              editor,
              pos,
              key: `scratchpad-block-${metadata.id}`,
              side: -1,
              // The row hosts focusable controls inside the contenteditable, so keep ProseMirror from handling its events or syncing selection into it.
              stopEvent: () => true,
              ignoreSelection: true,
              props: {
                ...metadata,
                onRedirectTo,
                latestAddedDataPointSource,
                readonly: !editor.isEditable,
                isActive: pos === activePos,
                isDatapoint: node.type.name === "dataPoint",
                source: (node.attrs.source as ScratchpadSource | null) ?? null,
                datapointLabel: typeof node.attrs.label === "string" ? node.attrs.label : "",
                updateDatapointLabel: (label: string) => {
                  if (!editor.options.editable) return;
                  const current = currentDataPoint(metadata.id);
                  if (current == null || current.node.attrs.label === label) return;

                  editor.view.dispatch(
                    editor.state.tr.setNodeMarkup(current.pos, undefined, { ...current.node.attrs, label }),
                  );
                },
                focusContent: () => {
                  const current = currentDataPoint(metadata.id);
                  if (current == null) return false;

                  editor.commands.setTextSelection(current.pos + 1);
                  requestAnimationFrame(() => {
                    if (!editor.isDestroyed) editor.view.focus();
                  });
                  return true;
                },
              },
            }),
          );
        });

        return decorations;
      },
    };
  },
  addProseMirrorPlugins() {
    const editor = this.editor;
    const name = this.name;

    return [
      new Plugin({
        key: new PluginKey("scratchpadBlockMetadata"),
        // setEditable updates the view without a transaction, so rebuild the widgets' readonly prop here.
        view: (view) => {
          let editable = view.editable;
          return {
            update: (view) => {
              if (view.editable === editable) return;
              editable = view.editable;
              editor.commands.updateDecorations(name);
            },
          };
        },
        appendTransaction(transactions, oldState, state) {
          if (!transactions.some((transaction) => transaction.docChanged)) return null;

          const previousBlocks = new Map<string, ProseMirrorNode>();
          oldState.doc.forEach((node) => {
            if (typeof node.attrs.id === "string") previousBlocks.set(node.attrs.id, node);
          });

          const seenIds = new Set<string>();
          const transaction = state.tr;

          const now = new Date().toISOString();

          state.doc.forEach((node, pos) => {
            const id = typeof node.attrs.id === "string" ? node.attrs.id : undefined;
            const isEmptyParagraph = node.type.name === "paragraph" && node.content.size === 0;
            if (id == null && isEmptyParagraph) return;

            const previousBlock = id == null ? undefined : previousBlocks.get(id);
            const hasDuplicateId = id != null && seenIds.has(id);

            // New empty paragraphs are scaffolding; emptied existing blocks keep their identity.
            if (isEmptyParagraph && (previousBlock == null || hasDuplicateId)) {
              if (id != null) {
                transaction.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  id: null,
                  createdAt: null,
                  updatedAt: null,
                });
              }

              return;
            }

            if (id == null || hasDuplicateId)
              transaction.setNodeMarkup(pos, undefined, { ...node.attrs, id: v4(), createdAt: now, updatedAt: null });
            else if (
              previousBlock != null &&
              previousBlock !== node &&
              !previousBlock.type
                .create(
                  { ...previousBlock.attrs, ...getWidgetMetadata(node) },
                  previousBlock.content,
                  previousBlock.marks,
                )
                .eq(node)
            )
              transaction.setNodeMarkup(pos, undefined, { ...node.attrs, updatedAt: now });

            if (id != null) seenIds.add(id);
          });

          return transaction.docChanged ? transaction : null;
        },
        props: {
          transformPasted: (slice) => new Slice(freshContent(slice.content), slice.openStart, slice.openEnd),
        },
      }),
    ];
  },
});

// Tiptap's configure() accepts partial options, so the extension stays private and this factory is the only way to
// build it, which makes the widget renderer a compile-time requirement.
export function createBlockMetadata(options: ScratchpadBlockMetadataOptions) {
  return BlockMetadata.configure(options);
}
